import { BulkRecordOnlyModal } from "@/components/shared/BulkRecordOnlyModal";
import { getGrammarFormPublicationState } from "@/components/slides/publication/grammarFormPublicationState";
import { useSelection } from "@/contexts/SelectionContext";
import { useDuplicateLesson } from "@/lib/hooks/lessons/useDuplicateLesson";
import { useEditLesson } from "@/lib/hooks/lessons/useEditLesson";
import { useDuplicateProject } from "@/lib/hooks/projects/useDuplicateProject";
import { useUpdateProject } from "@/lib/hooks/projects/useUpdateProject";
import { useDuplicateSlide } from "@/lib/hooks/slides/useDuplicateSlide";
import { useNestSlides } from "@/lib/hooks/slides/useNestSlides";
import { useUpdateSlide } from "@/lib/hooks/slides/useUpdateSlide";
import {
  getGrammarFormValidationIssues,
  hasGraphQLErrorCode,
  summarizeGrammarFormValidationIssues,
} from "@/lib/utils/graphqlErrors";
import { scrollToItem } from "@/lib/utils/scrollToItem";
import { isFluencyReadingSlide, isSubQuizMaster } from "@/lib/utils/subQuizMerge";
import { useAppDispatch } from "@/store/hooks";
import {
  openAddSubslideModal,
  openEditLessonModal,
  openEditProjectModal,
  openEditSlideModal,
  openNestSlideModal,
} from "@/store/uiSlice";
import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";
import type { SlideFields } from "@/types/Slide";
import { ErrorCodes } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaUnlink } from "react-icons/fa";
import { LuCopyPlus } from "react-icons/lu";
import {
  MdCheckBoxOutlineBlank,
  MdEdit,
  MdKeyboardVoice,
  MdMergeType,
  MdOutlineContentCopy,
  MdOutlineDelete,
  MdOutlineDriveFileMove,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdPostAdd,
  MdPublish,
  MdVerticalAlignTop,
} from "react-icons/md";

const POSITION_CLASSES = {
  top: "-top-10",
  bottom: "-bottom-10",
} as const;

const ALIGNMENT_CLASSES = {
  left: "left-0",
  right: "right-0",
  center: "left-1/2 -translate-x-1/2",
} as const;

interface Action {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

interface CardActionBarProps {
  id: string;
  type: "project" | "lesson" | "slide";
  project?: ProjectFields;
  lesson?: LessonFields;
  slide?: SlideFields;
}

export function CardActionBar({ id, type, project, lesson, slide }: CardActionBarProps) {
  const dispatch = useAppDispatch();
  const {
    toggleProject,
    toggleLesson,
    toggleSlide,
    selectProject,
    selectLesson,
    selectSlide,
    toggleMoveModal,
    toggleTrashModal,
    toggleMoveSlideModal,
    toggleDuplicateSlideModal,
    toggleDuplicateLessonModal,
    toggleDuplicateProjectModal,
    toggleIsSelectionMode,
    toggleUnlinkModalOpen,
    clearAllSelections,
  } = useSelection();
  const navigate = useNavigate();
  const {
    toggleSlidesIsHidden,
    publishGrammarFormSlides,
    toggleLoading: updateSlidesIsHiddenLoading,
    publishLoading,
  } = useUpdateSlide();
  const { toggleLessonIsHidden, loading: toggleLessonIsHiddenLoading } = useEditLesson();
  const { toggleProjectIsHidden, toggleProjectIsHiddenLoading } = useUpdateProject();

  const { duplicateLesson, loading: duplicateLessonLoading } = useDuplicateLesson();
  const { duplicateProject, loading: duplicateProjectLoading } = useDuplicateProject();
  const { duplicateSlide, loading: duplicateSlideLoading } = useDuplicateSlide();
  const { unnestSlides, loading: nestLoading } = useNestSlides();
  const [showRecordOnly, setShowRecordOnly] = useState(false);

  const isLoading =
    duplicateLessonLoading ||
    duplicateProjectLoading ||
    duplicateSlideLoading ||
    updateSlidesIsHiddenLoading ||
    toggleLessonIsHiddenLoading ||
    toggleProjectIsHiddenLoading ||
    nestLoading ||
    publishLoading;

  // Grammar-form slides carry an explicit publication bit: a DRAFT (not yet published) replaces the
  // usual Hide/Show action with Publish. Linked copies can't be published directly (the server
  // rejects it — publication is inherited from the original), so they keep the normal Hide/Show.
  const grammarFormPublicationState =
    type === "slide" && slide ? getGrammarFormPublicationState(slide) : "not_applicable";
  const isGrammarFormDraft =
    type === "slide" &&
    !!slide &&
    !slide.isLinkedCopy &&
    (grammarFormPublicationState === "blocked_draft" ||
      grammarFormPublicationState === "ready_draft");

  const hideOrPublishAction: Action = isGrammarFormDraft
    ? {
        id: "hide",
        icon: MdPublish,
        label: "Publish",
        variant: "ghost",
        onClick: async (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (grammarFormPublicationState === "blocked_draft") {
            toast.error("Fix the content errors before publishing.");
            navigate({ to: "/admin/slide/$slideId", params: { slideId: id } });
            return;
          }

          try {
            await publishGrammarFormSlides([id]);
            toast.success("Published");
          } catch (error) {
            if (hasGraphQLErrorCode(error, ErrorCodes.GRAMMAR_FORM_NOT_PUBLISHABLE)) {
              const validationIssues = getGrammarFormValidationIssues(error);
              toast.error(
                validationIssues
                  ? summarizeGrammarFormValidationIssues(validationIssues)
                  : "This slide is not publishable."
              );
            } else if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
              toggleUnlinkModalOpen({ type: "Slide", itemId: id });
            } else {
              toast.error("Failed to publish.");
            }
          }
        },
        disabled: isLoading,
      }
    : {
        id: "hide",
        icon: (
          type === "slide"
            ? slide?.isHidden
            : type === "lesson"
              ? lesson?.isHidden
              : project?.isHidden
        )
          ? MdOutlineVisibility
          : MdOutlineVisibilityOff,
        label: (
          type === "slide"
            ? slide?.isHidden
            : type === "lesson"
              ? lesson?.isHidden
              : project?.isHidden
        )
          ? "Show"
          : "Hide",
        variant: "ghost",
        onClick: async (e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (type === "project" && project) {
              await toggleProjectIsHidden(id, !project.isHidden);
            } else if (type === "lesson" && lesson) {
              await toggleLessonIsHidden(id, !lesson.isHidden);
            } else if (type === "slide" && slide) {
              await toggleSlidesIsHidden([id], !slide.isHidden);
            }
            toast.success(
              !(type === "project"
                ? project?.isHidden
                : type === "lesson"
                  ? lesson?.isHidden
                  : slide?.isHidden)
                ? "Hidden"
                : "Shown"
            );
          } catch (error) {
            if (
              type === "slide" &&
              hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)
            ) {
              toggleUnlinkModalOpen({ type: "Slide", itemId: id });
            } else {
              toast.error("Failed to update visibility.");
            }
          }
        },
        disabled: isLoading,
      };

  const ACTIONS: Action[] = [
    {
      id: "select",
      icon: MdCheckBoxOutlineBlank,
      label: "Select",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleProject(id, project?.isLinkedCopy);
          toggleIsSelectionMode(true);
        } else if (type === "lesson" && lesson) {
          toggleLesson(id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
          toggleIsSelectionMode(true);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          toggleSlide(id, slide.isLinkedCopy, slide.sortOrder);
          toggleIsSelectionMode(true);
        }
      },
    },
    {
      // Joins this slide to another main slide's group; the two then travel together.
      id: "nest",
      icon: MdPostAdd,
      label: "Make subslide",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "slide" && slide) {
          dispatch(openNestSlideModal({ ids: [slide.id], label: slide.name }));
        }
      },
    },
    {
      id: "unnest",
      icon: MdVerticalAlignTop,
      label: "Promote to slide",
      variant: "ghost",
      onClick: async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "slide" && slide) {
          await unnestSlides([slide.id]);
        }
      },
      disabled: isLoading,
    },
    {
      // Creates a slide that always plays with this one, instead of a slide of the lesson.
      id: "addSubslide",
      icon: MdPostAdd,
      label: "Add subslide",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "slide" && slide) {
          dispatch(openAddSubslideModal({ id: slide.id, name: slide.name }));
        }
      },
    },
    {
      id: "move",
      icon: MdOutlineDriveFileMove,
      label: "Move",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Select the item first before opening move modal
        if (type === "project" && project) {
          selectProject(id, project.isLinkedCopy);
          toggleMoveModal(true);
        } else if (type === "lesson" && lesson) {
          selectLesson(id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
          toggleMoveModal(true);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          selectSlide(id, slide.isLinkedCopy, slide.sortOrder);
          toggleMoveSlideModal(true);
        }
      },
    },
    {
      id: "duplicate",
      icon: MdOutlineContentCopy,
      label: "Duplicate",
      variant: "ghost",
      onClick: (e) => {
        if (type === "project" && project) {
          selectProject(id, project.isLinkedCopy);
          toggleDuplicateProjectModal(project.name);
        } else if (type === "lesson" && lesson) {
          selectLesson(id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
          toggleDuplicateLessonModal(lesson.name);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          selectSlide(id, slide.isLinkedCopy, slide.sortOrder);
          toggleDuplicateSlideModal(slide.name);
        }
        e.preventDefault();
        e.stopPropagation();
      },
    },
    {
      id: "copy",
      icon: LuCopyPlus,
      label: "Copy here",
      variant: "ghost",
      onClick: async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          await duplicateProject(
            project.id,
            project.parentId,
            project.name,
            "unique",
            project.sortOrder != null ? project.sortOrder : null
          );
        } else if (type === "lesson" && lesson) {
          await duplicateLesson(
            lesson.id,
            lesson.parentId,
            lesson.name,
            "unique",
            lesson.sortOrder != null ? lesson.sortOrder : null
          );
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          await duplicateSlide(
            slide.id,
            slide.lessonId,
            slide.name,
            "unique",
            null,
            slide.sortOrder
          );
        }
      },
      disabled: isLoading,
    },
    hideOrPublishAction,
    {
      id: "recordOnly",
      icon: MdKeyboardVoice,
      label: "Bulk record-only",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowRecordOnly(true);
      },
      disabled: isLoading,
    },
    {
      id: "edit",
      icon: MdEdit,
      label: "Edit",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          if (project.isLinkedCopy) {
            toggleUnlinkModalOpen({
              type: "Project",
              itemId: project.id,
              originalItemId: project.originalProjectId,
            });
            return;
          }
          dispatch(openEditProjectModal(project));
        } else if (type === "lesson" && lesson) {
          if (lesson.isLinkedCopy) {
            toggleUnlinkModalOpen({
              type: "Lesson",
              itemId: lesson.id,
              originalItemId: lesson.originalLessonId,
            });
            return;
          }
          dispatch(openEditLessonModal(lesson));
        } else if (type === "slide" && slide && slide.id) {
          if (slide.isLinkedCopy && slide.originalSlideId) {
            toggleUnlinkModalOpen({
              type: "Slide",
              itemId: slide.id,
              originalItemId: slide.originalSlideId,
            });
            return;
          }
          dispatch(openEditSlideModal(slide));
        }
      },
    },
    {
      id: "unlink",
      icon: FaUnlink,
      label: "Unlink",
      variant: "ghost",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === "project" && project) {
          toggleUnlinkModalOpen({
            type: "Project",
            itemId: project.id,
            originalItemId: project.originalProjectId,
          });
        } else if (type === "lesson" && lesson) {
          toggleUnlinkModalOpen({
            type: "Lesson",
            itemId: lesson.id,
            originalItemId: lesson.originalLessonId,
          });
        } else if (type === "slide" && slide?.originalSlideId) {
          toggleUnlinkModalOpen({
            type: "Slide",
            itemId: slide.id,
            originalItemId: slide.originalSlideId,
          });
        }
      },
    },
    {
      id: "delete",
      icon: MdOutlineDelete,
      label: "Trash",
      variant: "danger",
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (type === "project" && project) {
          selectProject(id, project.isLinkedCopy);
        } else if (type === "lesson" && lesson) {
          selectLesson(id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
        } else if (type === "slide" && slide?.isLinkedCopy !== undefined) {
          selectSlide(id, slide.isLinkedCopy, slide.sortOrder);
        }

        toggleTrashModal(true);
      },
    },
  ];

  const isLinkedCopies =
    type === "slide"
      ? slide?.isLinkedCopy
      : type === "project"
        ? project?.isLinkedCopy
        : lesson?.isLinkedCopy;

  // The "merge quizzes" action only makes sense on a slide that can HOLD subQuizzes (other /
  // reading-fluency, non-linked). Drop it everywhere else.
  const canMerge = type === "slide" && !!slide && isSubQuizMaster(slide);

  // Nesting is one level deep: only a main slide takes subslides, only a subslide is promoted out.
  const isMainSlide = type === "slide" && !!slide && !slide.parentSlideId;
  const isSubslide = type === "slide" && !!slide && !!slide.parentSlideId;

  const cardActions = (
    isLinkedCopies
      ? ACTIONS.filter((a) => ["select", "hide", "unlink", "delete"].includes(a.id))
      : ACTIONS.filter((a) => a.id !== "unlink")
  )
    .filter((a) => a.id !== "merge" || canMerge)
    .filter((a) => a.id !== "addSubslide" || isMainSlide)
    .filter((a) => a.id !== "nest" || isMainSlide)
    .filter((a) => a.id !== "unnest" || isSubslide)
    .filter((a) => a.id !== "recordOnly" || type !== "slide")
    // A subslide moves or duplicates only with the group its main slide owns.
    .filter((a) => !isSubslide || !["move", "duplicate", "copy"].includes(a.id));

  return (
    <>
      <div
        className={`absolute opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200
          panel inline-flex items-center gap-0.5 px-1.5 py-1.5 z-50 bg-slate-50
          ${POSITION_CLASSES.top}
          ${ALIGNMENT_CLASSES.center}`}
        role="toolbar"
        aria-label="Card actions"
      >
        {cardActions.map((action) => (
          <div key={action.id} className="relative group/action">
            <IconButton
              onClick={(e) => {
                clearAllSelections();
                action.onClick(e);
              }}
              variant={action.variant ?? "ghost"}
              size="sm"
              aria-label={action.label}
              disabled={action.disabled}
            >
              <action.icon className="w-4 h-4" />
            </IconButton>
            <div
              className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 px-2 py-1
              bg-surface text-secondary-200 caption rounded-md whitespace-nowrap
              opacity-0 group-hover/action:opacity-100 transition-opacity
              pointer-events-none z-20"
              role="tooltip"
            >
              {action.label}
            </div>
          </div>
        ))}
      </div>

      {showRecordOnly && type !== "slide" && (
        <BulkRecordOnlyModal type={type} id={id} onClose={() => setShowRecordOnly(false)} />
      )}
    </>
  );
}
